import React from 'react';
import MultiSelect from './MultiSelect';

const FilterControls = ({ 
  filters, 
  onFiltersChange, 
  hashtagOptions, 
  speakerOptions,
  onClearFilters,
  resultCounts 
}) => {
  const handleDateChange = (field, value) => {
    onFiltersChange({
      ...filters,
      dateRange: {
        ...filters.dateRange,
        [field]: value
      }
    });
  };

  const handleHashtagsChange = (selectedHashtags) => {
    onFiltersChange({
      ...filters,
      selectedHashtags
    });
  };

  const handleSpeakersChange = (selectedSpeakers) => {
    onFiltersChange({
      ...filters,
      selectedSpeakers
    });
  };

  const hasActiveFilters = 
    filters.dateRange.start || 
    filters.dateRange.end || 
    filters.selectedHashtags.length > 0 || 
    filters.selectedSpeakers.length > 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4 transition-colors duration-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="text-sm text-red-600 hover:text-red-800 font-medium"
          >
            Clear All Filters
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Date Range
          </label>
          <div className="space-y-2">
            <input
              type="date"
              value={filters.dateRange.start || ''}
              onChange={(e) => handleDateChange('start', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Start date"
            />
            <input
              type="date"
              value={filters.dateRange.end || ''}
              onChange={(e) => handleDateChange('end', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="End date"
            />
          </div>
        </div>

        {/* Hashtags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Hashtags
          </label>
          <MultiSelect
            options={hashtagOptions}
            selectedValues={filters.selectedHashtags}
            onChange={handleHashtagsChange}
            placeholder="Select hashtags..."
          />
        </div>

        {/* Speakers */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Speakers
          </label>
          <MultiSelect
            options={speakerOptions}
            selectedValues={filters.selectedSpeakers}
            onChange={handleSpeakersChange}
            placeholder="Select speakers..."
          />
        </div>

        {/* Result Counts */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Results
          </label>
          <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex justify-between">
              <span>Meetings:</span>
              <span className="font-medium">{resultCounts.meetings}</span>
            </div>
            <div className="flex justify-between">
              <span>Agenda Items:</span>
              <span className="font-medium">{resultCounts.agendaItems}</span>
            </div>
            <div className="flex justify-between">
              <span>Speeches:</span>
              <span className="font-medium">{resultCounts.speeches}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-2">
            {filters.dateRange.start && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                From: {new Date(filters.dateRange.start).toLocaleDateString()}
                <button
                  onClick={() => handleDateChange('start', '')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}
            {filters.dateRange.end && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                To: {new Date(filters.dateRange.end).toLocaleDateString()}
                <button
                  onClick={() => handleDateChange('end', '')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}
            {filters.selectedHashtags.map(hashtag => (
              <span key={hashtag} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {hashtag}
                <button
                  onClick={() => handleHashtagsChange(filters.selectedHashtags.filter(h => h !== hashtag))}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            ))}
            {filters.selectedSpeakers.map(speaker => (
              <span key={speaker} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {speaker}
                <button
                  onClick={() => handleSpeakersChange(filters.selectedSpeakers.filter(s => s !== speaker))}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterControls;
